import { useState, useEffect, useCallback } from "react";
import { api } from "../api";
import { logError } from "../utils/logger";

/**
 * Hook for fetching a single note by ID
 * Used for note detail page when sharing links
 *
 * @param {string} noteId - The ID of the note to fetch
 * @returns {Object} { data, loading, error, refetch }
 */
export function useNote(noteId) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch the note
  const fetchNote = useCallback(async () => {
    if (!noteId) {
      setError("No note ID provided");
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await api.getNote(noteId);
      setData(response.data);
    } catch (err) {
      setError(err.message || "Failed to load note");
      logError("Failed to fetch note:", err);
    } finally {
      setLoading(false);
    }
  }, [noteId]);

  // Fetch on mount and when noteId changes
  useEffect(() => {
    fetchNote();
  }, [fetchNote]);

  return {
    data,
    loading,
    error,
    refetch: fetchNote,
  };
}
